import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useAlbums } from '../contexts/AlbumContext';
import { motion } from 'framer-motion';
import { format } from 'date-fns';

const Dashboard = () => {
  const { user } = useAuth();
  const { getUserAlbums, deleteAlbum } = useAlbums();
  const navigate = useNavigate();
  const [userAlbums, setUserAlbums] = useState([]);

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }

    const albums = getUserAlbums(user.id);
    setUserAlbums(albums);
  }, [user, navigate, getUserAlbums]);

  const handleDeleteAlbum = async (albumId) => {
    if (window.confirm('Are you sure you want to delete this album?')) {
      await deleteAlbum(albumId);
      const updatedAlbums = getUserAlbums(user.id);
      setUserAlbums(updatedAlbums);
    }
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-white py-12">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          {/* Header - Cleaned up */}
          <div className="flex justify-between items-start mb-12 pb-8 border-b border-gray-200">
            <div>
              <h1 className="text-4xl font-light text-black mb-2 tracking-tight">Dashboard</h1>
              <p className="text-gray-600 font-light">Welcome back, {user.name}</p>
            </div>
          </div>

          {/* Beta Notice */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-12">
            <div className="flex items-start">
              <div className="w-5 h-5 bg-green-400 rounded-full mr-4 mt-0.5 flex-shrink-0"></div>
              <div>
                <h3 className="text-sm font-medium text-green-900 mb-1">Beta Access Active</h3>
                <p className="text-sm text-green-700 font-light">
                  You have unlimited access to all features during our beta period. Help us improve by sharing your feedback!
                </p>
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            <div className="text-center p-6 border border-gray-200 rounded-lg">
              <div className="text-3xl font-light text-black mb-2">{userAlbums.length}</div>
              <div className="text-sm text-gray-600 font-light">Albums Created</div>
            </div>
            <div className="text-center p-6 border border-gray-200 rounded-lg">
              <div className="text-3xl font-light text-black mb-2">
                {userAlbums.reduce((total, album) => total + album.photos.length, 0)}
              </div>
              <div className="text-sm text-gray-600 font-light">Total Photos</div>
            </div>
            <div className="text-center p-6 border border-gray-200 rounded-lg">
              <div className="text-3xl font-light text-black mb-2">∞</div>
              <div className="text-sm text-gray-600 font-light">Beta Limits</div>
            </div>
          </div>

          {/* Albums */}
          <div>
            <h2 className="text-2xl font-light text-black mb-8">Your Albums</h2>

            {userAlbums.length === 0 ? (
              <div className="text-center py-20 border border-gray-200 rounded-lg">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <div className="grid grid-cols-2 gap-1 w-8 h-8">
                    <div className="bg-gray-400 rounded-sm"></div>
                    <div className="bg-gray-400 rounded-sm"></div>
                    <div className="bg-gray-400 rounded-sm"></div>
                    <div className="bg-gray-400 rounded-sm"></div>
                  </div>
                </div>
                <h3 className="text-xl font-light text-black mb-3">No albums yet</h3>
                <p className="text-gray-600 mb-8 font-light">Create your first album to start collecting photos</p>
                <Link
                  to="/create-album"
                  className="bg-black text-white px-6 py-3 rounded-md hover:bg-gray-800 font-medium transition-all btn-primary inline-block"
                >
                  Create Your First Album
                </Link>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {userAlbums.map((album) => (
                  <motion.div
                    key={album.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-all group"
                  >
                    <div className="aspect-video bg-gray-50 flex items-center justify-center border-b border-gray-200">
                      {album.photos.length > 0 ? (
                        <div className="grid grid-cols-2 gap-1 w-full h-full p-4">
                          {album.photos.slice(0, 4).map((photo, index) => (
                            <div key={index} className="bg-white rounded overflow-hidden">
                              <img
                                src={photo.url}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-black">
                          <div className="grid grid-cols-2 gap-2 w-12 h-12">
                            <div className="bg-black rounded"></div>
                            <div className="bg-black rounded"></div>
                            <div className="bg-black rounded"></div>
                            <div className="bg-black rounded"></div>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-6">
                      <h3 className="text-lg font-medium text-black mb-2">{album.title}</h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2 font-light">{album.description}</p>
                      
                      <div className="flex items-center justify-between text-sm text-gray-500 mb-6 font-light">
                        <span>{album.photos.length} photos</span>
                        <span>{format(new Date(album.createdAt), 'MMM d, yyyy')}</span>
                      </div>

                      <div className="flex space-x-3">
                        <Link
                          to={`/album/${album.id}`}
                          className="flex-1 bg-black text-white px-4 py-2 rounded-md hover:bg-gray-800 text-center text-sm font-medium transition-all btn-primary"
                        >
                          View Album
                        </Link>
                        <button
                          onClick={() => handleDeleteAlbum(album.id)}
                          className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-sm font-medium text-gray-700 transition-all btn-secondary"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Dashboard;